class BacktestChartStorage {
  constructor() {
    this.storageKey = 'botspot_backtest_charts';
    this.maxCharts = 20;
    this.maxDataPoints = 5000; // With intelligent resampling, handles any length backtest
    this.expiryHours = 24;
  }

  isStorageAvailable() {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      const isQuotaExceeded = e && (
        e.name === 'QuotaExceededError' ||
        e.code === 22 ||
        e.code === 1014 ||
        (typeof e.message === 'string' && e.message.toLowerCase().includes('quota'))
      );

      if (isQuotaExceeded) {
        console.warn('localStorage quota exceeded but still accessible:', e);
        return true;
      }

      console.warn('localStorage not available:', e);
      return false;
    }
  }

  getAllCharts() {
    if (!this.isStorageAvailable()) return {};

    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : {};
    } catch (e) {
      console.error('Error reading chart data:', e);
      return {};
    }
  }

  saveAllCharts(charts) {
    if (!this.isStorageAvailable()) return false;

    try {
      const filtered = this.cleanupOldCharts(charts);
      localStorage.setItem(this.storageKey, JSON.stringify(filtered));
      return true;
    } catch (e) {
      console.error('Error saving chart data:', e);
      if (e.name === 'QuotaExceededError') {
        this.emergencyCleanup();
      }
      return false;
    }
  }

  getChart(backtestId) {
    const charts = this.getAllCharts();
    return charts[backtestId] || null;
  }

  addDataPoint(backtestId, percentageComplete, portfolioValue, simulationDate = null, positions = null, cash = null) {
    if (!backtestId || percentageComplete === null || portfolioValue === null) return false;

    const charts = this.getAllCharts();

    // Debug logging for testing resampling
    if (typeof window !== 'undefined' && window.debugChartStorage) {
      console.log(`[ChartStorage] Adding point: ${percentageComplete}% = $${portfolioValue} @ ${simulationDate}`);
    }

    if (!charts[backtestId]) {
      charts[backtestId] = {
        id: backtestId,
        startTime: Date.now(),
        lastUpdate: Date.now(),
        data: []
      };
    }

    const chart = charts[backtestId];
    chart.lastUpdate = Date.now();

    // Add new data point (avoid duplicates at same percentage)
    const lastPoint = chart.data[chart.data.length - 1];
    if (!lastPoint || lastPoint.x !== percentageComplete) {
      chart.data.push({
        x: percentageComplete,
        y: portfolioValue,
        date: simulationDate,
        positions: positions, // Store positions at this point in time
        cash: cash // Store cash value for when no positions held
      });

      // Intelligent data point management for long backtests
      if (chart.data.length > this.maxDataPoints) {
        // Debug logging when resampling occurs
        if (typeof window !== 'undefined' && window.debugChartStorage) {
          console.log(`[ChartStorage] Resampling from ${chart.data.length} to ~${this.maxDataPoints} points`);
        }

        const originalData = chart.data;
        const targetPoints = this.maxDataPoints;
        const sampledData = [];

        // Always keep first point
        sampledData.push(originalData[0]);

        // Calculate step size for even distribution
        // We want targetPoints total, already have first and will add last
        // So we need targetPoints - 2 intermediate points
        const step = (originalData.length - 1) / (targetPoints - 1);

        // Add evenly distributed intermediate points
        for (let i = 1; i < targetPoints - 1; i++) {
          const index = Math.round(i * step);
          if (index < originalData.length - 1) {
            sampledData.push(originalData[index]);
          }
        }

        // Always keep last point
        sampledData.push(originalData[originalData.length - 1]);

        if (typeof window !== 'undefined' && window.debugChartStorage) {
          console.log(`[ChartStorage] Resampled to ${sampledData.length} points with even distribution`);
          console.log(`[ChartStorage] Points at percentages:`, sampledData.map(p => p.x.toFixed(1) + '%').join(', '));
        }

        chart.data = sampledData;
      }
    }

    return this.saveAllCharts(charts);
  }

  clearChart(backtestId) {
    const charts = this.getAllCharts();
    delete charts[backtestId];
    return this.saveAllCharts(charts);
  }

  cleanupOldCharts(charts) {
    const now = Date.now();
    const expiryMs = this.expiryHours * 60 * 60 * 1000;
    const filtered = {};

    // Remove expired charts
    Object.entries(charts).forEach(([id, chart]) => {
      if (chart.lastUpdate && (now - chart.lastUpdate) < expiryMs) {
        filtered[id] = chart;
      }
    });

    // If still too many, keep only the most recent ones
    const sortedIds = Object.keys(filtered).sort((a, b) =>
      (filtered[b].lastUpdate || 0) - (filtered[a].lastUpdate || 0)
    );

    const finalFiltered = {};
    sortedIds.slice(0, this.maxCharts).forEach(id => {
      finalFiltered[id] = filtered[id];
    });

    return finalFiltered;
  }

  emergencyCleanup() {
    console.warn('Emergency cleanup: clearing oldest charts');
    const charts = this.getAllCharts();

    // Keep only 5 most recent charts in emergency
    const sortedIds = Object.keys(charts).sort((a, b) =>
      (charts[b].lastUpdate || 0) - (charts[a].lastUpdate || 0)
    );

    const emergency = {};
    sortedIds.slice(0, 5).forEach(id => {
      emergency[id] = charts[id];
    });

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(emergency));
    } catch (e) {
      // Last resort: clear everything
      console.error('Emergency cleanup failed, clearing all chart data');
      localStorage.removeItem(this.storageKey);
    }
  }

  getStorageSize() {
    if (!this.isStorageAvailable()) return 0;

    const data = localStorage.getItem(this.storageKey);
    return data ? new Blob([data]).size : 0;
  }
}

export default BacktestChartStorage;
